import { Suspense, lazy, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { AsHtml } from "@ww-digital/applanga-js";

import t from "../../../utils/translate";
import { setCookieValue, SECONDS_IN_THIRTY_DAYS } from "../../../utils/cookies";
import routes from "../../../routes";
import useAppContext from "../../AppContext";
import RoundButton from "../../RoundButton";
import { DEFAULT_INITIAL_WEIGHT_LB } from "../constants";
import BulletItem from "../../BulletItem";
import {
  getEstimatedFinalWeight,
  getEstimatedWeightLossData
} from "./WeightLossGraph/utils";
import Column from "../../Column";
import SiteWrapper from "../../SiteWrapper";
import Loading from "../../Loading";
import SequenceFooter from "../../SequenceFooter";
import EliSequenceChart from "../EliSequenceChart";
import StickyFooter from "components/StickyFooter";

import styles from "./styles.module.scss";

const WeightLossGraph = lazy(
  () => /* webpackChunkName: "weightlossgraph" */ import("./WeightLossGraph")
);

export default function ResultsJoinSequence() {
  const history = useHistory();
  const { profile, newQuiz } = useAppContext();

  const initialWeightLb =
    profile?.imperial?.weight || DEFAULT_INITIAL_WEIGHT_LB;
  const weightLossData = getEstimatedWeightLossData(initialWeightLb);
  const estimatedFinalWeight = getEstimatedFinalWeight(initialWeightLb);
  const estimatedWeightLost = Math.floor(
    weightLossData[0].weight - Math.floor(weightLossData[9].weight)
  );
  const isUni3or4Quiz = newQuiz.isUni3 || newQuiz.isUni4;

  const onContinue = () => {
    history.push(routes.clinicCheckoutTreatmentOptions);
  };

  useEffect(() => {
    setCookieValue("ww_results", { shown: true }, SECONDS_IN_THIRTY_DAYS);
  }, []);

  const renderWeightLossGraph = () => {
    if (isUni3or4Quiz)
      return <EliSequenceChart renderCaption={false} renderHeader={false} />;
    else return <WeightLossGraph initialWeightLb={initialWeightLb} />;
  };

  const transformHtml = html => {
    if (isUni3or4Quiz) {
      return html.replace(
        /<strong>(.*?)<\/strong>/g,
        `<span class="${styles.bluecornText}">$1</span>`
      );
    } else return html;
  };

  const renderFooter = () => {
    if (isUni3or4Quiz)
      return (
        <StickyFooter
          className={styles.unifooter}
          nextButtonProps={{ onNext: onContinue, label: t("CONTINUE") }}
        />
      );
    else
      return (
        <SequenceFooter className={styles.footer}>
          <RoundButton onClick={onContinue}>{t("CONTINUE")}</RoundButton>
        </SequenceFooter>
      );
  };

  return (
    <SiteWrapper size="small">
      <Column className={styles.content}>
        <h1 className={styles.heading}>
          {t("JOIN_SEQUENCE_ELIGIBLE_HEADER", {
            firstName: profile.firstName
          })}
        </h1>
        <p className={styles.text}>{t("JOIN_SEQUENCE_ELIGIBLE_TEXT")}</p>
        <ul className={styles.bulletList}>
          <BulletItem isCircle={isUni3or4Quiz}>
            <AsHtml
              value={transformHtml(t("JOIN_SEQUENCE_ELIGIBLE_BULLET_1"))}
              tagType="p"
              interpolations={{
                weightLoss: estimatedWeightLost
              }}
            />
          </BulletItem>
          <BulletItem isCircle={isUni3or4Quiz}>
            <AsHtml
              value={transformHtml(t("JOIN_SEQUENCE_ELIGIBLE_BULLET_2"))}
              tagType="p"
              interpolations={{ weight: estimatedFinalWeight }}
            />
          </BulletItem>
          <BulletItem isCircle={isUni3or4Quiz}>
            {t("JOIN_SEQUENCE_ELIGIBLE_BULLET_3")}
          </BulletItem>
        </ul>
        <Suspense fallback={<Loading loading />}>
          {renderWeightLossGraph()}
        </Suspense>
        <p className={styles.disclaimer}>
          {t("JOIN_SEQUENCE_ELIGIBLE_DISCLAIMER")}
        </p>
        {renderFooter()}
      </Column>
    </SiteWrapper>
  );
}
